import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RestaurantCard from './RestaurantCard';
import { Restaurant } from '../types';

/** Get today's date as YYYY-MM-DD */
function getTodayStr(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

/** Filter restaurant lunch items to only today and future dates */
function filterToCurrentAndFuture(restaurants: Restaurant[]): Restaurant[] {
  const todayStr = getTodayStr();
  return restaurants.map((r) => {
    if (!r.lunchItems || r.lunchItems.length === 0) return r;
    const filtered = r.lunchItems.filter((item) => !item.date || item.date >= todayStr);
    return { ...r, lunchItems: filtered.length > 0 ? filtered : null };
  });
}

interface RestaurantListProps {
  restaurants: Restaurant[];
  isLoading: boolean;
  total: number;
  favoriteIds?: Set<string>;
  onToggleFavorite?: (restaurantId: string) => void;
  onLocate?: (lat: number, lng: number) => void;
}

export default function RestaurantList({
  restaurants,
  isLoading,
  total,
  favoriteIds,
  onToggleFavorite,
  onLocate,
}: RestaurantListProps) {
  if (isLoading) {
    return (
      <Fade in timeout={300}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
          <CircularProgress size={36} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Haetaan ravintoloita...
          </Typography>
        </Box>
      </Fade>
    );
  }

  if (total === 0) {
    return (
      <Fade in timeout={300}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1.5 }}>
          <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Ravintoloita ei löytynyt hakukriteereillä.
          </Typography>
          <Typography variant="body2" color="text.disabled" textAlign="center">
            Kokeile laajentaa sädettä tai muuttaa hakua.
          </Typography>
        </Box>
      </Fade>
    );
  }

  const filtered = filterToCurrentAndFuture(restaurants);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
        {total} ravintolaa
      </Typography>
      {filtered.map((restaurant, i) => (
        <RestaurantCard
          key={restaurant.restaurantId}
          restaurant={restaurant}
          index={i}
          isFavorite={favoriteIds?.has(restaurant.restaurantId)}
          onToggleFavorite={onToggleFavorite}
          onLocate={onLocate}
        />
      ))}
    </Box>
  );
}
